import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined"
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined"
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined"
import CircularProgress from "@mui/material/CircularProgress"
import { useMemo } from "react"
import { useSelector } from "react-redux"

import Row from "../../../common/components/Row"
import { canAccessLeadTargeting, isAccountOwner } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import MyMlsBoostCampaignSummary from "../my-mls/MyMlsBoostCampaignSummary"
import { useMyMlsBoostCampaign } from "../my-mls/useMyMlsBoostCampaign"
import { selectCRMSettings } from "../reducer"
import BrokerageProfileIntakeSummary from "./BrokerageProfileIntakeSummary"
import CampaignEmptyState from "./CampaignEmptyState"
import CampaignEntityPanel from "./CampaignEntityPanel"
import CampaignScriptSection from "./CampaignScriptSection"
import ProfileColumn from "./ProfileColumn"
import {
  leadTargetingEditPath,
  recruitingProfileEditPath,
} from "./campaignNavigation"
import { RECRUIT_PROFILE_SECTION_TITLES } from "./recruitProfileSectionStyles"
import { useBrokerageProfileIntake } from "./useBrokerageProfileIntake"

function formatTimestamp(iso) {
  if (!iso) {
    return null
  }
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    })
  } catch {
    return null
  }
}

export default function CampaignTab() {
  const profile = useSelector(selectProfile)
  const accountIsOwner = isAccountOwner(profile)
  const showLeadTargeting = canAccessLeadTargeting(profile)
  const { isLoading, intake, submittedAt, hasSubmitted } =
    useBrokerageProfileIntake()
  const crmSettings = useSelector(selectCRMSettings)
  const mlsFallback = useMemo(() => {
    const mlss = crmSettings?.settings?.mlss
    if (!Array.isArray(mlss) || mlss.length === 0) {
      return ""
    }
    return mlss
      .map((mls) => mls?.name)
      .filter(Boolean)
      .join(", ")
  }, [crmSettings])
  const {
    isLoading: isBoostCampaignLoading,
    criteria: boostCriteria,
    updatedAt: boostUpdatedAt,
    hasSaved: hasBoostCampaignSaved,
  } = useMyMlsBoostCampaign()

  const profileUpdatedLabel = useMemo(() => {
    if (!hasSubmitted) {
      return null
    }
    const at = formatTimestamp(submittedAt)
    return at ? `Submitted ${at}` : null
  }, [hasSubmitted, submittedAt])

  const targetingUpdatedLabel = useMemo(() => {
    if (!hasBoostCampaignSaved) {
      return null
    }
    const at = formatTimestamp(boostUpdatedAt)
    return at ? `Saved ${at}` : null
  }, [hasBoostCampaignSaved, boostUpdatedAt])

  return (
    <ProfileColumn sx={{ gap: 3.5, width: "100%" }}>
      <CampaignScriptSection />

      <CampaignEntityPanel
        variant="recruitingProfile"
        icon={<PersonSearchOutlinedIcon />}
        title="Your Recruiting campaign profile"
        description="Brokerage identity, contact details, offices, and messaging — submitted from Profile setup."
        timestampLabel={profileUpdatedLabel}
        editTo={accountIsOwner ? recruitingProfileEditPath() : undefined}
        editLabel={hasSubmitted ? "Edit profile" : "Set up profile"}
      >
        {isLoading ? (
          <Row sx={{ justifyContent: "center", py: 5 }}>
            <CircularProgress size={32} />
          </Row>
        ) : !hasSubmitted ? (
          <CampaignEmptyState
            variant="recruitingProfile"
            icon={<PersonSearchOutlinedIcon />}
            title="No campaign profile yet"
          >
            On the <strong>Profile</strong> tab, use{" "}
            <strong>Set up your Recruiting Campaign profile</strong> to add your
            website, complete your details, and submit.
          </CampaignEmptyState>
        ) : (
          <BrokerageProfileIntakeSummary intake={intake} />
        )}
      </CampaignEntityPanel>

      {showLeadTargeting ? (
        <CampaignEntityPanel
          variant="leadTargeting"
          icon={<TravelExploreOutlinedIcon />}
          title={RECRUIT_PROFILE_SECTION_TITLES.leadTargeting}
          description="Geography, radius, sales thresholds, and brand exclusions — saved from My Leads."
          timestampLabel={targetingUpdatedLabel}
          editTo={leadTargetingEditPath()}
          editLabel={
            hasBoostCampaignSaved ? "Edit targeting" : "Set up targeting"
          }
        >
          {isBoostCampaignLoading ? (
            <Row sx={{ justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Row>
          ) : !hasBoostCampaignSaved ? (
            <CampaignEmptyState
              variant="leadTargeting"
              icon={<CampaignOutlinedIcon />}
              title="No lead targeting saved"
            >
              Open <strong>My Leads</strong> and use{" "}
              <strong>Lead Targeting for Campaign</strong> to set your filters.
            </CampaignEmptyState>
          ) : (
            <MyMlsBoostCampaignSummary
              criteria={boostCriteria}
              mlsFallback={mlsFallback}
            />
          )}
        </CampaignEntityPanel>
      ) : null}
    </ProfileColumn>
  )
}
